// v2 → v3 migration: maps everything the v2 calculator ever wrote onto the v3 sizing state, on every
// load path. Pure and math-free — this module must NOT import any engine math (engine.ts,
// use-case-engine.ts, share.ts, value-story.ts), so the deck can consume it without crossing the
// calculator→deck boundary. It reads the v3 value story only through value-story-schema.ts.
//
// Load paths (one entry point each):
//   migrateSharePayload   `?s=` link, after engine/share.ts decodeShare()
//   migrateSession        `clay-calc-session-v1` sessionStorage envelope
//   migrateConfigImport   the saved-settings markdown file (`<!-- clay:settings … -->` trailer)
//   migrateSavedStory     Story_JSON__c read back from Salesforce (v2-calculator `v: 1`, or v3 `v: 2`)
//   migrateDeckSnapshot   the `clay-value-story-v1` localStorage snapshot the deck reads
//   migrateBenchmarkRows  seed + local benchmark rows (`clay-bm-rows-v1` / `clay-bm-seedv`)
//
// Every path funnels into one mapper (mapV2). The rules, from the spec's "Migration" section:
//  - A v2 answer is carried only when its v3 question means the same thing. Unit changes are
//    converted only when they are exact (per-month ↔ per-year ×12, per-quarter ×4, a capacity
//    multiple → per-SDR volume, a reply multiple → relative lift). Anything else is DROPPED with a
//    visible re-check flag — never silently reset.
//  - Levers become use cases (LEVER_MAP). Retired levers (li5, li7, li13, li14) are dropped, flagged.
//  - Realization / attribution carry per lever only when exactly one lever maps to the use case;
//    when two fold in, the use case takes its defaults and both are flagged.
//  - Only INCLUDED levers become selected use cases, and only their answers are read.
//  - A v3 input (share/session/config payload `v: 3`, story `v: 2`) passes through unchanged.
//
// The v2 facts this needs (lever ids, question keys, v2 defaults) are frozen below rather than
// imported, so v2's catalog can be deleted once nothing else imports it.

import { z } from 'zod';

import { BM_SEEDS, BM_SEED_V } from '../data/benchmarks';
import { DEFAULT_CURRENT_ANNUAL_PIPELINE, getSizedUseCase } from '../data/use-case-catalog';
import { safeParseJson } from '../../_shared/parse-json';
import { BmRowSchema } from './boundary-schemas';
import { ValueStorySchema, type ValueStory } from './value-story-schema';
import type { DealEconomics, UseCaseSelection } from './use-case-engine';

/* ---------------------------------------------------------------------------- v3 state */

/** Version marker of the v3 share / session / config payload (distinct from the story's `v: 2`). */
export const V3_PAYLOAD_VERSION = 3 as const;

const NumOrBlank = z.union([z.number().finite(), z.literal('')]);

export const RecheckSchema = z.object({
  /** The v3 use case the flag belongs to; null for deal-level / global flags. */
  useCaseId: z.string().nullable(),
  /** A v3 question key, or: realization | attribution | scenario | currentAnnualPipeline | tools |
   *  selection | timeMode | attributionSeed | custom-lever | `v2:<key>` (a dropped answer that has
   *  no v3 counterpart). */
  key: z.string(),
  kind: z.enum(['dropped', 'new', 'conflict', 'factors', 'scenario', 'retired', 'unsupported', 'estimate']),
  /** Human-readable reason, shown to the SE. */
  reason: z.string(),
  /** The v2 source, e.g. `li3.open_opps_year` (absent for flags with no single source). */
  from: z.string().optional(),
  /** The v2 value that was dropped or replaced (so the SE can re-enter it). */
  dropped: NumOrBlank.optional(),
});
export type Recheck = z.infer<typeof RecheckSchema>;

const SelectionSchema = z.object({
  id: z.string(),
  recordLevels: z.array(z.enum(['accounts', 'contacts'])).optional(),
  /** Calculation methods on (GTM automation). Absent = the first method only, so old payloads load as A only. */
  methods: z.array(z.string()).optional(),
  answers: z.record(z.string(), NumOrBlank).optional(),
  realization: NumOrBlank.nullable().optional(),
  attribution: NumOrBlank.nullable().optional(),
});

/** The v3 sizing state — structurally the input of computeCalculator(). */
export const SizingStateSchema = z.object({
  customerName: z.string(),
  deal: z.object({
    acv: z.number().finite(),
    winRate: z.number().finite(),
    contractEstimate: z.number().finite().nullable(),
    currentAnnualPipeline: z.number().finite().nullable(),
  }),
  selected: z.array(SelectionSchema),
  tools: z.record(z.string(), NumOrBlank).nullable(),
  customTools: z.array(z.object({ name: z.string(), spend: NumOrBlank })),
});
export type SizingState = z.infer<typeof SizingStateSchema>;

/** The v3 share / session / config payload: the sizing state plus any open re-check flags. */
export const V3PayloadSchema = SizingStateSchema.extend({
  v: z.literal(V3_PAYLOAD_VERSION),
  recheck: z.array(RecheckSchema).default([]),
});
export type V3Payload = z.infer<typeof V3PayloadSchema>;

export interface MigratedSizing {
  sizing: SizingState;
  recheck: Recheck[];
  /** 'v2' when the input was v2 data; null when it was already v3 and passed through. */
  migratedFrom: 'v2' | null;
}

export interface SessionMigration {
  migrated: MigratedSizing;
  linkedAccount: LinkedAccountShape | null;
  demoMode: boolean;
}

export type DeckSnapshotMigration =
  | { kind: 'story'; story: ValueStory }
  | { kind: 'legacy'; migrated: MigratedSizing }
  | { kind: 'invalid' };

// Compile-time guarantee that the sizing state feeds the engine unchanged.
type _DealMatches = SizingState['deal'] extends DealEconomics ? true : never;
type _SelectionMatches = SizingState['selected'][number] extends UseCaseSelection ? true : never;
const _typeChecks: [_DealMatches, _SelectionMatches] = [true, true];
void _typeChecks;

/* ------------------------------------------------------------------- frozen v2 / v3 facts */

type RecordLevel = 'accounts' | 'contacts';

const UC = {
  crm: 'uc-crm-enrichment',
  scoring: 'uc-account-lead-scoring',
  tam: 'uc-tam-sourcing',
  inbound: 'uc-inbound-speed-to-lead',
  outbound: 'uc-automated-outbound',
  perf: 'uc-performance-marketing',
  signal: 'uc-signal-based-outbound',
  plg: 'uc-plg-expansion',
  tools: 'uc-tool-consolidation',
  gtm: 'uc-gtm-automation',
} as const;

/** Output order: maturity tier (Crawl → Walk → Run → Efficiency), as the catalog lists them. */
const UC_ORDER: string[] = [UC.crm, UC.scoring, UC.tam, UC.inbound, UC.outbound, UC.perf, UC.signal, UC.tools, UC.gtm];

/** v2 company-profile defaults (engine/share.ts defProfile). */
const V2_DEFAULT_ACV = 40000;
const V2_DEFAULT_WIN_RATE = 25;
/** v2 profile attribution seed default; a non-default value is flagged because the seed is gone. */
const V2_DEFAULT_ATTRIBUTION_SEED = 10;
/** v2's `rz` global realization seed default. */
const V2_DEFAULT_RZ = 100;
/** Levers whose v2 default attribution was 100% (the rest fell back to the profile seed). */
const V2_FULL_ATTRIBUTION = new Set(['li15', 'li16']);
/** li15's old per-person hours default, used when folding a legacy `people` count into team hours. */
const LEGACY_LI15_HOURS_PER_PERSON = 6;

export const RETIRED_LEVERS: Record<string, { name: string; reason: string }> = {
  li5: {
    name: 'Pre-meeting research coverage',
    reason: 'Retired in v3 (parked for the unsized Pre-call prep use case).',
  },
  li7: { name: 'Email bounce rate', reason: 'Retired in v3; there is no email-deliverability use case.' },
  li13: { name: '# accounts health-scored', reason: 'Retired in v3; retention is out of scope.' },
  li14: { name: 'Champion / job-change tracking', reason: 'Retired in v3; champion tracking is out of scope.' },
};

/** One v2 answer → one v3 question. `fn` is applied only for exact unit conversions. */
interface KeyRule {
  to: string;
  fn?: (v: number) => number;
  /** Raised as a re-check even though the answer is carried (meaning is close, not identical). */
  note?: string;
}

interface Target {
  uc: string;
  /** The record level this lever switches on for the use case. */
  level?: RecordLevel;
  keys: Record<string, KeyRule>;
  /** v2 answers with no v3 equivalent: key → { review (v3 key or `v2:<key>`), reason }. */
  dropped?: Record<string, { review: string; reason: string }>;
  /** Several v2 answers combine exactly into v3 answers. `sources`: any answered triggers it. */
  derive?: {
    sources: string[];
    defaults: Record<string, number>;
    compute: (get: (key: string) => number) => Record<string, number>;
    note?: Record<string, string>;
  };
  /** v3 questions with no v2 source: flagged when they end up uncarried, with why it matters. */
  newInV3?: Record<string, string>;
}

const MEETING_TO_OPP_NEW =
  'New in v3: v2 counted every meeting as pipeline; v3 converts meetings to opportunities at this rate (default 30%).';

/** v2 lever → v3 targets. li2 splits across two use cases. The single source of the mapping. */
const LEVER_MAP: Record<string, Target[]> = {
  li6: [
    {
      uc: UC.crm,
      level: 'accounts',
      keys: {
        total_records: { to: 'accounts_records' },
        current_fill: { to: 'accounts_fill_current' },
        clay_fill: { to: 'accounts_fill_clay' },
        activation_pct: { to: 'accounts_record_to_opp' },
      },
    },
  ],
  li2: [
    {
      uc: UC.crm,
      level: 'contacts',
      keys: {
        current_coverage: { to: 'contacts_reachable_current' },
        clay_coverage: { to: 'contacts_reachable_clay' },
        meeting_rate: { to: 'contacts_to_meeting' },
      },
      newInV3: {
        contacts_records: 'New in v3: the number of contact records in the CRM (v2 sized from sourcing volume).',
        contacts_share_worked: 'New in v3: share of newly reachable contacts that your reps work (default 25%).',
        contacts_meeting_to_opp: MEETING_TO_OPP_NEW,
      },
    },
    {
      uc: UC.tam,
      level: 'contacts',
      keys: {
        contacts_month: {
          to: 'contacts_year_clay',
          fn: (v) => v * 12,
          note: 'v2 had one contact-volume figure with no today / with-Clay split; it is entered as the with-Clay volume per year — set today’s volume.',
        },
        meeting_rate: { to: 'contacts_to_meeting' },
      },
      newInV3: {
        contacts_year_today: 'New in v3: contacts sourced per year today (v2 counted all of them as new).',
        contacts_meeting_to_opp: MEETING_TO_OPP_NEW,
      },
    },
  ],
  li3: [
    {
      uc: UC.scoring,
      keys: {
        current_scored: { to: 'coverage_current' },
        clay_scored: { to: 'coverage_clay' },
        winrate_lift: { to: 'winrate_lift' },
      },
      dropped: {
        open_opps_year: {
          review: 'currentAnnualPipeline',
          reason:
            'v2 sized from open opportunities worked per year; v3 reads the current annual pipeline in dollars. Check it under deal economics (it starts at a default).',
        },
      },
      newInV3: {
        currentAnnualPipeline: 'v3 reads the current annual pipeline (dollars) for this use case. Check it under deal economics (it starts at a default).',
      },
    },
  ],
  li1: [
    {
      uc: UC.tam,
      level: 'accounts',
      keys: {
        accounts_quarter: { to: 'accounts_year_clay', fn: (v) => v * 4 },
        pipeline_rate: { to: 'accounts_to_opp' },
      },
      newInV3: {
        accounts_year_today: 'New in v3: net-new accounts sourced per year today (v2 counted all of them as new).',
      },
    },
  ],
  li8: [
    {
      uc: UC.inbound,
      keys: {
        leads_month: { to: 'leads_year', fn: (v) => v * 12 },
        current_match: { to: 'match_current' },
        clay_match: { to: 'match_clay' },
        qualified_pct: { to: 'qualified_pct' },
      },
      dropped: {
        conversion_pct: {
          review: 'conversion_current',
          reason: 'v2 asked for qualified leads that convert; v3 asks for the lead-to-opportunity conversion of all leads.',
        },
      },
      newInV3: {
        conversion_current: 'v3 asks for the current lead-to-opportunity conversion of all inbound leads.',
        speed_lift: 'v3 asks for the relative conversion lift from speed-to-lead (default 25%).',
      },
    },
  ],
  li9: [
    {
      uc: UC.inbound,
      keys: {
        leads_month: { to: 'leads_year', fn: (v) => v * 12 },
        current_conv: { to: 'conversion_current' },
        conv_lift: { to: 'speed_lift' },
      },
      dropped: {
        current_response_min: {
          review: 'v2:current_response_min',
          reason: 'v3 does not ask for time to first touch; the speed-to-lead effect is the relative conversion lift.',
        },
      },
      newInV3: {
        qualified_pct: 'New for this lever: share of leads that are qualified (default 50%).',
        match_current: 'New for this lever: current inbound match rate (default 40%).',
        match_clay: 'New for this lever: inbound match rate with Clay (default 85%).',
      },
    },
  ],
  li11: [
    {
      uc: UC.outbound,
      keys: {
        meeting_rate: { to: 'meeting_rate' },
      },
      derive: {
        sources: ['sdrs', 'prospects_month', 'capacity_mult'],
        defaults: { sdrs: 5, prospects_month: 300, capacity_mult: 3 },
        compute: (get) => ({
          outreach_year: legacyOutreachYear(get('sdrs'), get('prospects_month'), get('prospects_month') * Math.max(get('capacity_mult'), 1)),
        }),
        note: {
          outreach_year: 'v2 asked for SDRs, prospects per SDR per month and an output multiple; they are converted to added contacts sequenced per year (SDRs × prospects × (multiple − 1) × 12).',
        },
      },
      newInV3: { meeting_to_opp: MEETING_TO_OPP_NEW },
    },
  ],
  li12: [
    {
      uc: UC.perf,
      keys: {
        ad_spend_month: { to: 'ad_spend_year', fn: (v) => v * 12 },
        current_match: { to: 'match_current' },
        clay_ad_match: { to: 'match_clay' },
        recoverable_pct: { to: 'recoverable_pct' },
      },
    },
  ],
  li4: [
    {
      uc: UC.signal,
      keys: {
        prospects_month: { to: 'signal_contacts_month' },
        meeting_rate: { to: 'meeting_rate_today' },
        // v2: incremental meetings = base × (multiple − 1); mapV2 turns this lift into the with-Clay rate = today × (1 + lift).
        reply_mult: { to: 'meeting_lift', fn: (v) => Math.max(v - 1, 0) * 100 },
      },
      newInV3: { meeting_to_opp: MEETING_TO_OPP_NEW },
    },
  ],
  li10: [
    {
      uc: UC.signal,
      keys: {
        meeting_rate: { to: 'meeting_rate_today' },
        rate_lift: {
          to: 'meeting_lift',
          note: 'v2’s lift came from hyperpersonal messaging; v3 asks for the with-Clay meeting rate from signal timing (v2’s lift is applied to today’s rate). Confirm the number still fits.',
        },
      },
      derive: {
        sources: ['sdrs', 'prospects_month'],
        defaults: { sdrs: 5, prospects_month: 300 },
        compute: (get) => ({ signal_contacts_month: get('sdrs') * get('prospects_month') }),
        note: {
          signal_contacts_month: 'v2 asked for SDRs × prospects per SDR per month; it is entered as signal-triggered contacts per month.',
        },
      },
      newInV3: { meeting_to_opp: MEETING_TO_OPP_NEW },
    },
  ],
  li16: [{ uc: UC.tools, keys: {} }],
  li15: [
    {
      uc: UC.gtm,
      keys: {
        annual_cost: { to: 'annual_cost' },
        hours_week: { to: 'hours_week' },
        automation_pct: { to: 'automation_pct' },
      },
    },
  ],
};

/** Every v2 lever id the migration knows, in catalog order. */
const LEVER_ORDER = ['li1', 'li2', 'li3', 'li4', 'li5', 'li6', 'li7', 'li8', 'li9', 'li10', 'li11', 'li12', 'li13', 'li14', 'li15', 'li16'];

/** The v2 levers that map onto a v3 use case (everything except the retired ones). */
export const MAPPED_LEVERS: readonly string[] = Object.keys(LEVER_MAP);

/** The v2 → v3 question-key table, exported for the report and the catalog-consistency test. */
export function questionKeyMap(): { lever: string; uc: string; from: string; to: string; exact: string }[] {
  const out: { lever: string; uc: string; from: string; to: string; exact: string }[] = [];
  for (const lever of LEVER_ORDER) {
    for (const t of LEVER_MAP[lever] ?? []) {
      for (const [from, r] of Object.entries(t.keys)) {
        const legacy = LEGACY_LIFTS.find((l) => l.uc === t.uc && l.lift === r.to);
        out.push({ lever, uc: t.uc, from, to: legacy ? legacy.clay : r.to, exact: legacy || r.fn ? 'converted' : r.note ? 'carried + flagged' : 'identical' });
      }
      for (const s of t.derive?.sources ?? []) out.push({ lever, uc: t.uc, from: s, to: Object.keys(t.derive!.compute(() => 1)).join('+'), exact: 'derived' });
      for (const [from, d] of Object.entries(t.dropped ?? {})) out.push({ lever, uc: t.uc, from, to: `(dropped → ${d.review})`, exact: 'dropped' });
    }
  }
  return out;
}

/* ------------------------------------------------------------- legacy relative-lift answers */

/**
 * Signal outbound and PLG used to ask a RELATIVE lift (`meeting_lift`, `conversion_lift`); they now
 * ask the with-Clay rate itself. A saved lift converts exactly: with Clay = today × (1 + lift/100),
 * where today is the answered value or the catalog default.
 */
interface LegacyLift {
  uc: string;
  lift: string;
  today: string;
  clay: string;
}
const LEGACY_LIFTS: LegacyLift[] = [
  { uc: UC.signal, lift: 'meeting_lift', today: 'meeting_rate_today', clay: 'meeting_rate_clay' },
  { uc: UC.plg, lift: 'conversion_lift', today: 'conversion_today', clay: 'conversion_clay' },
];

const roundRate = (v: number) => Math.round(v * 1e6) / 1e6;

function legacyLiftFor(ucId: string): LegacyLift | undefined {
  return LEGACY_LIFTS.find((l) => l.uc === ucId);
}

/** Today's rate for a lift conversion: the answer, else the catalog default (a blank answer is 0). */
function todayRate(rule: LegacyLift, answers: Record<string, number | ''>): number {
  const a = answers[rule.today];
  if (a === '') return 0;
  if (typeof a === 'number') return a;
  return getSizedUseCase(rule.uc)?.questions.find((q) => q.key === rule.today)?.def ?? 0;
}

const withLift = (today: number, lift: number) => roundRate((today * (100 + lift)) / 100);

/** Answers with any legacy relative-lift key converted to the with-Clay rate (an answered rate wins). */
export function upgradeLegacyAnswers(
  ucId: string,
  answers: Record<string, number | ''> | undefined,
): Record<string, number | ''> | undefined {
  const rule = legacyLiftFor(ucId);
  if (!answers || !rule || !(rule.lift in answers)) return answers;
  const { [rule.lift]: lift, ...rest } = answers;
  if (rule.clay in rest) return rest;
  return { ...rest, [rule.clay]: withLift(todayRate(rule, rest), lift === '' ? 0 : lift) };
}

/* ------------------------------------------------------ legacy automated-outbound volume */

/**
 * Automated outbound used to ask SDRs and contacts per SDR per month (today and with Clay); it now
 * asks the outreach volume sent per year. The old chain's "added contacts per year" converts
 * exactly: (with Clay − today, floored at 0) × SDRs × 12. Unanswered old keys take the old defaults.
 */
const LEGACY_OUTBOUND_KEYS = ['sdrs', 'contacts_sdr_month_today', 'contacts_sdr_month_clay'] as const;
const LEGACY_OUTBOUND_DEFAULTS = { sdrs: 5, contacts_sdr_month_today: 300, contacts_sdr_month_clay: 900 };

function legacyOutreachYear(sdrs: number, today: number, clay: number): number {
  return Math.max(0, clay - today) * sdrs * 12;
}

/** Answers with the legacy SDR keys converted to `outreach_year` (an answered volume wins). */
export function upgradeOutboundAnswers(
  answers: Record<string, number | ''> | undefined,
): Record<string, number | ''> | undefined {
  if (!answers || !LEGACY_OUTBOUND_KEYS.some((k) => k in answers)) return answers;
  const rest = { ...answers };
  const get = (k: (typeof LEGACY_OUTBOUND_KEYS)[number]) => {
    const v = rest[k];
    delete rest[k];
    return v === undefined ? LEGACY_OUTBOUND_DEFAULTS[k] : v === '' ? 0 : v;
  };
  const volume = legacyOutreachYear(get('sdrs'), get('contacts_sdr_month_today'), get('contacts_sdr_month_clay'));
  return 'outreach_year' in rest ? rest : { ...rest, outreach_year: volume };
}

/** Every selection's answers upgraded (the v3 load paths: share, session, config, saved story). */
function upgradeSelections<T extends { id: string; answers?: Record<string, number | ''> }>(selected: T[]): T[] {
  return selected.map((s) => {
    if (s.id === UC.outbound) {
      const answers = upgradeOutboundAnswers(s.answers);
      return answers === s.answers ? s : { ...s, answers };
    }
    const rule = legacyLiftFor(s.id);
    if (!rule || !s.answers || !(rule.lift in s.answers)) return s;
    return { ...s, answers: upgradeLegacyAnswers(s.id, s.answers) };
  });
}

/* --------------------------------------------------------------------- the v2 normal form */

/** What every v2 load path is reduced to before mapping. Pure data, already sanitised. */
interface V2State {
  customerName: string;
  dealSize: number;
  winRate: number;
  clayCost: number | null;
  attributionSeed: number;
  included: string[];
  answers: Record<string, Record<string, number | ''>>;
  scenarios: Record<string, string>;
  /** Explicit per-lever overrides only (a blank / absent entry means "default"). */
  realizationById: Record<string, number>;
  attributionById: Record<string, number>;
  /** The global realization seed when it is not the v2 default. */
  realizationSeed: number | null;
  tools: Record<string, number | ''> | null;
  customTools: { name: string; spend: number | '' }[];
  customLeverNames: string[];
  timeAsTime: boolean;
  /** Extra flags raised while normalising (e.g. tools recovered from rounded text). */
  notes: Recheck[];
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const num = (v: unknown): number | null => (isNum(v) ? v : null);
const rec = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

function cleanAnswers(raw: unknown): Record<string, number | ''> {
  const out: Record<string, number | ''> = {};
  for (const [k, v] of Object.entries(rec(raw))) {
    if (v === '') out[k] = '';
    else if (isNum(v)) out[k] = v;
  }
  return out;
}

function cleanFactors(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(rec(raw))) if (isNum(v) && v >= 0) out[k] = v;
  return out;
}

/** li15 used to ask per-person hours plus a headcount; fold into team hours (mirrors v2 migrate.ts). */
function foldLi15People(a: Record<string, number | ''>): void {
  const people = a.people;
  if (people === '' || people == null || !Number.isFinite(Number(people))) return;
  const hrs = a.hours_week === '' || a.hours_week == null ? LEGACY_LI15_HOURS_PER_PERSON : Number(a.hours_week);
  if (Number.isFinite(hrs)) a.hours_week = hrs * Number(people);
  delete a.people;
}

function firstString(...vs: unknown[]): string | null {
  for (const v of vs) if (typeof v === 'string' && v) return v;
  return null;
}

function cleanTools(raw: unknown): Record<string, number | ''> | null {
  if (raw == null) return null;
  const out: Record<string, number | ''> = {};
  for (const [k, v] of Object.entries(rec(raw))) {
    if (v === '') out[k] = '';
    else if (isNum(v)) out[k] = v;
  }
  return out;
}

function cleanCustomTools(raw: unknown): { name: string; spend: number | '' }[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c) => {
    const o = rec(c);
    const spend = o.spend === '' ? '' : isNum(o.spend) ? o.spend : null;
    return typeof o.name === 'string' && spend !== null ? [{ name: o.name, spend }] : [];
  });
}

/* ------------------------------------------------------------ v2 share payload (zod parse) */

/** The v2 share payload (types.ts SharePayload). Records stay `unknown` and are sanitised after. */
const V2SharePayloadSchema = z.looseObject({
  p: z.record(z.string(), z.unknown()).nullish(),
  a: z.record(z.string(), z.unknown()).nullish(),
  s: z.record(z.string(), z.unknown()).nullish(),
  i: z.record(z.string(), z.unknown()).nullish(),
  tl: z.record(z.string(), z.unknown()).nullish(),
  ct: z.array(z.unknown()).nullish(),
  cu: z.array(z.unknown()).nullish(),
  cn: z.string().nullish(),
  rz: z.unknown().optional(),
  rzm: z.record(z.string(), z.unknown()).nullish(),
  atm: z.record(z.string(), z.unknown()).nullish(),
  tm: z.unknown().optional(),
});
type V2SharePayload = z.infer<typeof V2SharePayloadSchema>;

function v2FromSharePayload(p: V2SharePayload): V2State {
  const prof = rec(p.p);
  const answers: V2State['answers'] = {};
  for (const [lever, a] of Object.entries(rec(p.a))) answers[lever] = cleanAnswers(a);
  const scenarios: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec(p.s))) if (typeof v === 'string') scenarios[k] = v;
  const rz = num(p.rz);
  return finishV2({
    customerName: p.cn || '',
    dealSize: num(prof.deal_size) ?? V2_DEFAULT_ACV,
    winRate: num(prof.win_rate) ?? V2_DEFAULT_WIN_RATE,
    clayCost: num(prof.clay_cost),
    attributionSeed: num(prof.attribution) ?? V2_DEFAULT_ATTRIBUTION_SEED,
    // `i` carries 1 from nonDefaultPayload but raw booleans from copyShareLink.
    included: Object.entries(rec(p.i)).filter(([, v]) => !!v).map(([k]) => k),
    answers,
    scenarios,
    realizationById: cleanFactors(p.rzm),
    attributionById: cleanFactors(p.atm),
    realizationSeed: rz != null && rz !== V2_DEFAULT_RZ ? rz : null,
    // No `tl` means the v2 default checklist (untouched) — v3 `null` says the same thing.
    tools: cleanTools(p.tl),
    customTools: cleanCustomTools(p.ct),
    customLeverNames: (p.cu ?? []).map((c) => firstString(rec(c).uc, rec(c).name, rec(c).id) ?? 'Custom use case'),
    timeAsTime: !!p.tm,
    notes: [],
  });
}

function finishV2(s: V2State): V2State {
  if (s.answers.li15) foldLi15People(s.answers.li15);
  return s;
}

/* ---------------------------------------------------------------- v2 story snapshot (v: 1) */

const V2SnapshotSchema = z.looseObject({
  v: z.literal(1).optional(),
  customerName: z.string().nullish(),
  profile: z.record(z.string(), z.unknown()).nullish(),
  rzPct: z.unknown().optional(),
  timeAsTime: z.unknown().optional(),
  clayCost: z.unknown().optional(),
  ucs: z.array(
    z.looseObject({
      id: z.string(),
      scenario: z.string().nullish(),
      rzPct: z.unknown().optional(),
      attributionPct: z.unknown().optional(),
      total: z.unknown().optional(),
      inputs: z.array(z.looseObject({ key: z.string().nullish(), value: z.unknown(), assumed: z.unknown().optional() })).nullish(),
      mathLines: z.array(z.string()).nullish(),
    }),
  ),
});

const TOOL_NAMES: Record<string, string> = {
  ZoomInfo: 'zoominfo',
  Apollo: 'apollo',
  '6sense': 'sixsense',
  Lusha: 'lusha',
  Clearbit: 'clearbit',
  LeadIQ: 'leadiq',
  Outreach: 'outreach',
};

/** "$60K" / "$1.2M" / "$900" — the money() text the v2 snapshot wrote into li16's mathLines. */
function parseMoneyText(t: string): number | null {
  const m = t.replace(/,/g, '').match(/^\$(-?\d+(?:\.\d+)?)([KM]?)$/);
  if (!m) return null;
  return Number(m[1]) * (m[2] === 'K' ? 1e3 : m[2] === 'M' ? 1e6 : 1);
}

function v2FromSnapshot(snap: z.infer<typeof V2SnapshotSchema>): V2State {
  const prof = rec(snap.profile);
  const profileAttr = num(prof.attribution) ?? V2_DEFAULT_ATTRIBUTION_SEED;
  const seedRz = num(snap.rzPct);
  const answers: V2State['answers'] = {};
  const scenarios: Record<string, string> = {};
  const realizationById: Record<string, number> = {};
  const attributionById: Record<string, number> = {};
  const notes: Recheck[] = [];
  let tools: Record<string, number | ''> | null = null;
  const customTools: V2State['customTools'] = [];
  for (const u of snap.ucs) {
    // Same rule as CalculatorApp.loadSnapshot(): assumed inputs are v2 defaults, not customer answers.
    const a: Record<string, number | ''> = {};
    for (const inp of u.inputs ?? []) {
      if (inp.key && !inp.assumed && (inp.value === '' || isNum(inp.value))) a[inp.key] = inp.value;
    }
    answers[u.id] = a;
    if (u.scenario && u.scenario !== 'base') scenarios[u.id] = u.scenario;
    // A snapshot stores RESOLVED factors, so a value equal to the v2 default is indistinguishable
    // from "never touched": treat it as the default (v3 has its own) rather than as an override.
    const rz = num(u.rzPct);
    if (rz != null && rz !== V2_DEFAULT_RZ) realizationById[u.id] = rz;
    const at = num(u.attributionPct);
    const atDefault = V2_FULL_ATTRIBUTION.has(u.id) ? 100 : profileAttr;
    if (at != null && at !== atDefault) attributionById[u.id] = at;
    if (u.id === 'li16') {
      // Tool rows survive only as rounded text ("ZoomInfo contract = $60K/yr").
      tools = {};
      for (const line of u.mathLines ?? []) {
        const m = line.match(/^(.+) contract = (\$[^/]+)\/yr$/);
        if (!m) continue;
        const spend = parseMoneyText(m[2]);
        if (spend == null) continue;
        const id = TOOL_NAMES[m[1]];
        if (id) tools[id] = spend;
        else customTools.push({ name: m[1], spend });
      }
      notes.push({
        useCaseId: UC.tools,
        key: 'tools',
        kind: 'estimate',
        reason: 'Tool contract values were recovered from rounded text in the saved story ($60K style). Confirm each one.',
        from: 'li16.mathLines',
      });
    }
  }
  const realizationSeed = Object.keys(realizationById).length === 0 && seedRz != null && seedRz !== V2_DEFAULT_RZ ? seedRz : null;
  return finishV2({
    customerName: snap.customerName || '',
    dealSize: num(prof.deal_size) ?? V2_DEFAULT_ACV,
    winRate: num(prof.win_rate) ?? V2_DEFAULT_WIN_RATE,
    clayCost: num(prof.clay_cost) ?? num(snap.clayCost),
    attributionSeed: profileAttr,
    included: snap.ucs.map((u) => u.id),
    answers,
    scenarios,
    realizationById,
    attributionById,
    realizationSeed,
    tools,
    customTools,
    customLeverNames: snap.ucs.filter((u) => /^c\d|^custom/i.test(u.id) && !LEVER_ORDER.includes(u.id)).map((u) => u.id),
    timeAsTime: !!snap.timeAsTime,
    notes,
  });
}

/* --------------------------------------------------------------------------- the mapper */

function pctOrNull(v: number | undefined): number | null {
  return v != null && v >= 0 ? v : null;
}

function mapV2(s: V2State): MigratedSizing {
  const recheck: Recheck[] = [...s.notes];
  const flagged = new Set<string>();
  const flag = (r: Recheck) => {
    const id = `${r.useCaseId ?? ''}|${r.key}|${r.kind}`;
    if (flagged.has(id) && r.kind !== 'conflict') return;
    flagged.add(id);
    recheck.push(r);
  };

  // Which levers are in play: included ones, in catalog order. Unknown ids are custom levers.
  const known = LEVER_ORDER.filter((id) => s.included.includes(id));
  const unknownIncluded = s.included.filter((id) => !LEVER_ORDER.includes(id));
  for (const id of unknownIncluded) flagCustom(flag, s.customLeverNames.find((n) => n === id) ?? id);
  for (const name of s.customLeverNames) if (!unknownIncluded.includes(name)) flagCustom(flag, name);

  for (const lever of known) {
    const retired = RETIRED_LEVERS[lever];
    if (retired) {
      flag({
        useCaseId: null,
        key: lever,
        kind: 'retired',
        reason: `${retired.name} (${lever}): ${retired.reason} Its inputs were not carried over.`,
        from: lever,
      });
    }
  }

  const byUc = new Map<string, { levers: string[]; targets: { lever: string; t: Target }[] }>();
  for (const lever of known) {
    for (const t of LEVER_MAP[lever] ?? []) {
      const e = byUc.get(t.uc) ?? { levers: [], targets: [] };
      if (!e.levers.includes(lever)) e.levers.push(lever);
      e.targets.push({ lever, t });
      byUc.set(t.uc, e);
    }
  }

  const selected: UseCaseSelection[] = [];
  let tools: Record<string, number | ''> | null = null;
  let customTools: V2State['customTools'] = [];

  for (const ucId of UC_ORDER) {
    const entry = byUc.get(ucId);
    if (!entry) continue;
    const out: Record<string, number | ''> = {};
    const provenance: Record<string, string> = {};

    for (const { lever, t } of entry.targets) {
      const saved = s.answers[lever] ?? {};
      const set = (to: string, value: number | '', from: string, note?: string) => {
        if (to in out) {
          if (out[to] !== value) {
            flag({
              useCaseId: ucId,
              key: to,
              kind: 'conflict',
              reason: `Two v2 levers answered this question differently; kept ${provenance[to]} (${out[to]}) and dropped ${from} (${value}).`,
              from,
              dropped: value,
            });
          }
          return;
        }
        out[to] = value;
        provenance[to] = from;
        if (note) flag({ useCaseId: ucId, key: to, kind: 'estimate', reason: note, from });
      };
      for (const [key, value] of Object.entries(saved)) {
        const rule = t.keys[key];
        if (rule) {
          const v = value === '' ? '' : rule.fn ? rule.fn(value) : value;
          set(rule.to, v, `${lever}.${key}`, rule.note);
          continue;
        }
        const drop = t.dropped?.[key];
        if (drop) {
          flag({
            useCaseId: ucId,
            key: drop.review,
            kind: 'dropped',
            reason: `${drop.reason} (v2 ${lever}.${key} = ${value === '' ? 'blank' : value} was not carried.)`,
            from: `${lever}.${key}`,
            dropped: value,
          });
        }
      }
      const d = t.derive;
      if (d && d.sources.some((k) => k in saved)) {
        const get = (k: string) => {
          const v = saved[k];
          return v === undefined ? d.defaults[k] : v === '' ? 0 : v;
        };
        for (const [to, v] of Object.entries(d.compute(get))) set(to, v, `${lever}.${d.sources.join('+')}`, d.note?.[to]);
      }
    }

    // The v2 lift answers become with-Clay rates (v3 asks the rate itself); flags follow the new key.
    const legacy = legacyLiftFor(ucId);
    if (legacy && legacy.lift in out) {
      const today = todayRate(legacy, out);
      for (const r of recheck) {
        if (r.useCaseId !== ucId || r.key !== legacy.lift) continue;
        r.key = legacy.clay;
        if (typeof r.dropped === 'number') r.dropped = withLift(today, r.dropped);
        if (r.kind === 'conflict') {
          r.reason = `Two v2 levers answered this question differently; kept ${provenance[legacy.lift]} and dropped ${r.from} (a v2 lift, converted to a with-Clay rate on today’s ${today}%).`;
        }
      }
      const converted = upgradeLegacyAnswers(ucId, out) ?? out;
      delete out[legacy.lift];
      Object.assign(out, converted);
    }

    // v3 questions nobody answered: say so where the v2 sizing never had the concept.
    for (const { t } of entry.targets) {
      for (const [key, reason] of Object.entries(t.newInV3 ?? {})) {
        if (key in out || recheck.some((r) => r.useCaseId === ucId && r.key === key)) continue;
        flag({ useCaseId: ucId, key, kind: 'new', reason });
      }
    }

    const sel: UseCaseSelection = { id: ucId };
    const levels = new Set(entry.targets.flatMap(({ t }) => (t.level ? [t.level] : [])));
    if (levels.size) sel.recordLevels = (['accounts', 'contacts'] as RecordLevel[]).filter((l) => levels.has(l));
    if (Object.keys(out).length) sel.answers = out;

    // Realization / attribution: carry only when one lever maps here; two folding in → defaults.
    if (entry.levers.length === 1) {
      const lever = entry.levers[0];
      const rz = pctOrNull(s.realizationById[lever]) ?? pctOrNull(s.realizationSeed ?? undefined);
      if (rz != null) sel.realization = rz;
      const at = pctOrNull(s.attributionById[lever]);
      if (at != null) sel.attribution = at;
      const scen = s.scenarios[lever];
      if (scen && scen !== 'base') flagScenario(flag, ucId, lever, scen);
    } else {
      const what = entry.levers.join(' + ');
      flag({
        useCaseId: ucId,
        key: 'realization',
        kind: 'factors',
        reason: `${what} folded into one use case; realization is back to the use case default. Re-set it.`,
      });
      flag({
        useCaseId: ucId,
        key: 'attribution',
        kind: 'factors',
        reason: `${what} folded into one use case; attribution is back to the use case default. Re-set it.`,
      });
      for (const lever of entry.levers) {
        const scen = s.scenarios[lever];
        if (scen && scen !== 'base') flagScenario(flag, ucId, lever, scen);
      }
    }
    selected.push(sel);

    if (ucId === UC.tools) {
      tools = s.tools;
      customTools = s.customTools;
    }
  }

  if (s.timeAsTime) {
    flag({
      useCaseId: null,
      key: 'timeMode',
      kind: 'unsupported',
      reason: 'Time mode (time savings shown as hours) is not in v3. Dollar values are used throughout.',
    });
  }
  if (s.attributionSeed !== V2_DEFAULT_ATTRIBUTION_SEED) {
    flag({
      useCaseId: null,
      key: 'attributionSeed',
      kind: 'dropped',
      reason: `The v2 profile attribution seed (${s.attributionSeed}%) is gone; each use case has its own default attribution. Set per-use-case attribution where it differed.`,
      from: 'profile.attribution',
      dropped: s.attributionSeed,
    });
  }

  const estimate = s.clayCost != null && s.clayCost > 0 ? s.clayCost : null;
  return {
    sizing: {
      customerName: s.customerName,
      deal: { acv: s.dealSize, winRate: s.winRate, contractEstimate: estimate, currentAnnualPipeline: DEFAULT_CURRENT_ANNUAL_PIPELINE },
      selected,
      tools,
      customTools,
    },
    recheck,
    migratedFrom: 'v2',
  };
}

function flagCustom(flag: (r: Recheck) => void, name: string): void {
  flag({
    useCaseId: null,
    key: `custom-lever:${name}`,
    kind: 'unsupported',
    reason: `Custom use case "${name}" is not in v3 (custom use cases are deferred). It was not carried over.`,
    from: name,
  });
}

function flagScenario(flag: (r: Recheck) => void, ucId: string, lever: string, scen: string): void {
  flag({
    useCaseId: ucId,
    key: 'scenario',
    kind: 'scenario',
    reason: `${lever} was on the ${scen} scenario (a ${scen === 'conservative' ? '×0.7' : scen === 'aggressive' ? '×1.3' : 'custom'} multiplier); v3 has no scenarios. Adjust realization instead.`,
    from: `${lever}.scenario`,
  });
}

/* --------------------------------------------------------------------- v3 pass-through */

/** A payload written before pipeline had a default (null) loads as the default, so it reads as Assumed. */
function withPipelineDefault<D extends { currentAnnualPipeline: number | null }>(deal: D): D {
  return { ...deal, currentAnnualPipeline: deal.currentAnnualPipeline ?? DEFAULT_CURRENT_ANNUAL_PIPELINE };
}

function passThrough(p: V3Payload): MigratedSizing {
  return {
    sizing: {
      customerName: p.customerName,
      deal: withPipelineDefault(p.deal),
      selected: upgradeSelections(p.selected),
      tools: p.tools,
      customTools: p.customTools,
    },
    recheck: p.recheck,
    migratedFrom: null,
  };
}

/** A saved v3 story → the sizing state that reproduces it (answers the customer actually gave). */
export function sizingFromValueStory(story: ValueStory): SizingState {
  const tc = story.useCases.find((u) => u.id === UC.tools);
  let tools: Record<string, number | ''> | null = null;
  const customTools: SizingState['customTools'] = [];
  if (tc?.displacedTools) {
    tools = {};
    for (const t of tc.displacedTools) {
      if (t.id) tools[t.id] = t.spend;
      else customTools.push({ name: t.name, spend: t.spend });
    }
  }
  return {
    customerName: story.customerName,
    deal: withPipelineDefault(story.deal),
    selected: upgradeSelections(story.useCases.map((u) => {
      const answers: Record<string, number | ''> = {};
      // Assumed inputs are catalog defaults, not customer answers — leave them to be re-derived.
      for (const i of u.inputs) if (!i.assumed) answers[i.key] = i.value;
      const sel: SizingState['selected'][number] = {
        id: u.id,
        realization: u.realizationPct,
        attribution: u.attributionPct,
      };
      if (u.recordLevels.length) sel.recordLevels = u.recordLevels;
      if (u.methods?.length) sel.methods = u.methods;
      if (Object.keys(answers).length) sel.answers = answers;
      return sel;
    })),
    tools,
    customTools,
  };
}

/* --------------------------------------------------------------------- entry points */

function asPayload(raw: unknown): { v3: V3Payload } | { v2: V2SharePayload } | null {
  if (raw && typeof raw === 'object' && (raw as { v?: unknown }).v === V3_PAYLOAD_VERSION) {
    const v3 = V3PayloadSchema.safeParse(raw);
    return v3.success ? { v3: v3.data } : null;
  }
  const v2 = V2SharePayloadSchema.safeParse(raw);
  return v2.success ? { v2: v2.data } : null;
}

/** Share-link payload (engine/share.ts decodeShare() output) → v3 sizing. Null when unusable. */
export function migrateSharePayload(raw: unknown): MigratedSizing | null {
  const p = asPayload(raw);
  if (!p) return null;
  return 'v3' in p ? passThrough(p.v3) : mapV2(v2FromSharePayload(p.v2));
}

const LinkedAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  opportunityId: z.string().optional(),
  opportunityName: z.string().optional(),
});
export type LinkedAccountShape = z.infer<typeof LinkedAccountSchema>;

/** The `clay-calc-session-v1` envelope (`{ share, linkedAccount, demoMode }`) or a pre-envelope bare payload. */
export function migrateSession(raw: unknown): SessionMigration | null {
  const o = rec(raw);
  const envelope = 'share' in o;
  const migrated = migrateSharePayload(envelope ? o.share : raw);
  if (!migrated) return null;
  const linked = envelope ? LinkedAccountSchema.safeParse(o.linkedAccount) : null;
  return {
    migrated,
    linkedAccount: linked?.success ? linked.data : null,
    demoMode: envelope ? o.demoMode === true : false,
  };
}

/** The saved-settings markdown (`<!-- clay:settings {json} -->` trailer) → v3 sizing. */
export function migrateConfigImport(markdown: string): MigratedSizing | null {
  const m = String(markdown || '').match(/<!--\s*clay:settings\s*([\s\S]*?)-->/);
  if (!m) return null;
  const parsed = safeParseJson(z.unknown(), m[1].trim());
  return parsed.success ? migrateSharePayload(parsed.data) : null;
}

function parseMaybeJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  const parsed = safeParseJson(z.unknown(), raw);
  return parsed.success ? parsed.data : undefined;
}

/** Story_JSON__c read back from Salesforce: a v2-calculator snapshot (`v: 1` or legacy) or a v3 story (`v: 2`). */
export function migrateSavedStory(raw: unknown): MigratedSizing | null {
  const data = parseMaybeJson(raw);
  if ((data as { v?: unknown } | null)?.v === 2) {
    const story = ValueStorySchema.safeParse(data);
    return story.success ? { sizing: sizingFromValueStory(story.data), recheck: [], migratedFrom: null } : null;
  }
  const snap = V2SnapshotSchema.safeParse(data);
  return snap.success ? mapV2(v2FromSnapshot(snap.data)) : null;
}

/**
 * The `clay-value-story-v1` deck snapshot. A v3 story (`v: 2`) is returned as-is for the deck to
 * render. A v2 snapshot cannot be rendered without engine math, so it is returned as migrated
 * sizing: the caller re-computes a v3 story from it (or asks the SE to reopen the calculator).
 */
export function migrateDeckSnapshot(raw: unknown): DeckSnapshotMigration {
  const data = parseMaybeJson(raw);
  if ((data as { v?: unknown } | null)?.v === 2) {
    const story = ValueStorySchema.safeParse(data);
    return story.success ? { kind: 'story', story: story.data } : { kind: 'invalid' };
  }
  const snap = V2SnapshotSchema.safeParse(data);
  return snap.success ? { kind: 'legacy', migrated: mapV2(v2FromSnapshot(snap.data)) } : { kind: 'invalid' };
}

/* ------------------------------------------------------------------- benchmark rows */

/**
 * A benchmark row as v3 stores it: v2's row plus `hidden`. Kept out of boundary-schemas.ts so the
 * v2 UI's `BmRow` type is untouched until the UI switches over; the switch-over should read and
 * write this schema (boundary-schemas' z.object strips `hidden`).
 */
export const BmRowV3Schema = BmRowSchema.extend({ hidden: z.boolean().optional() });
export type BmRow = z.infer<typeof BmRowV3Schema>;

/** v2 lever id → v3 use case id, for benchmark rows. One row has one metric, so li2 picks its headline. */
export const BENCHMARK_LEVER_TO_USE_CASE: Record<string, string> = {
  li1: UC.tam,
  // li2 splits across two use cases for sizing; its benchmark rows are about contact coverage.
  li2: UC.crm,
  li3: UC.scoring,
  li4: UC.signal,
  li6: UC.crm,
  li8: UC.inbound,
  li9: UC.inbound,
  li10: UC.signal,
  li11: UC.outbound,
  li12: UC.perf,
  li15: UC.gtm,
  li16: UC.tools,
};

/** The seed version that put rows on use case ids (v3's lever → use case remap). */
export const BM_SEED_V3 = '3';
/**
 * Seed version persisted to `clay-bm-seedv`. v4 reassigns nine seed rows to use cases that had no
 * proof points of their own (see SEED_RETAGS), so the deck's story cards have something to show.
 */
export const BM_SEED_V4 = '4';
/** Seed version persisted to `clay-bm-seedv` today. v5 adds the rows below to every stored table. */
export const BM_SEED_V5 = '5';
const SEED_V5_IDS = ['b75', 'b76', 'b77', 'b78', 'b79', 'b80'];

/** Appends the v5 seed rows a stored table does not have yet (by id, never overwriting a row). */
function appendV5Seeds(rows: BmRow[]): BmRow[] {
  const have = new Set(rows.map((r) => r.id));
  return [...rows, ...BM_SEEDS.filter((s) => SEED_V5_IDS.includes(s.id) && !have.has(s.id))];
}

/** One seed row's v3 → v4 move: the seed id, and the `uc-*` / lever id the v3 migration left it on. */
interface SeedRetag {
  id: string;
  /** Where a v3-migrated copy of this seed sits: a use case id, `other`, or a retired lever id (hidden). */
  was: string;
}

const SEED_RETAGS: readonly SeedRetag[] = [
  { id: 'b5', was: UC.signal },
  { id: 'b6', was: 'li5' },
  { id: 'b7', was: 'li5' },
  { id: 'b40', was: 'li5' },
  { id: 'b53', was: 'other' },
  { id: 'b58', was: UC.tools },
  { id: 'b59', was: UC.signal },
  { id: 'b61', was: UC.signal },
  { id: 'b69', was: 'other' },
];

/**
 * Re-tags stored copies of the moved seed rows onto their v4 use case. A stored row is only touched
 * while it still looks exactly like the v3 seed (old `metricId`, and `hidden` only for retired
 * levers): anyone who re-pointed or re-hid it keeps their choice. Everything else passes through.
 */
function retagMovedSeeds(rows: BmRow[]): BmRow[] {
  const moves = new Map(SEED_RETAGS.map((m) => [m.id, m]));
  return rows.map((r) => {
    const move = moves.get(r.id);
    const seed = move && BM_SEEDS.find((s) => s.id === move.id);
    if (!move || !seed || r.metricId !== move.was) return r;
    const retired = move.was in RETIRED_LEVERS;
    if (retired !== (r.hidden === true)) return r;
    const { hidden: _hidden, ...rest } = r;
    return { ...rest, metricId: seed.metricId, metricText: seed.metricText };
  });
}

/**
 * Remaps one benchmark row. Idempotent: rows already on `uc-*` ids (or `other` / custom ids) pass
 * through. Rows for retired levers keep their lever id (the lineage) and get `hidden: true` plus
 * the lever name as `metricText` so nothing is lost.
 */
export function migrateBenchmarkRow(r: BmRow): BmRow {
  const uc = BENCHMARK_LEVER_TO_USE_CASE[r.metricId];
  if (uc) return { ...r, metricId: uc };
  const retired = RETIRED_LEVERS[r.metricId];
  if (retired) return { ...r, hidden: true, metricText: r.metricText || retired.name };
  return r;
}

export interface BenchmarkMigration {
  rows: BmRow[];
  /** What to persist to `clay-bm-seedv`. */
  seedV: string;
  /** False when the stored rows were already migrated (nothing to write). */
  changed: boolean;
}

/**
 * Seed + local benchmark rows → v3. `storedRows` is `clay-bm-rows-v1` (null = still on the seed
 * catalog), `storedSeedV` is `clay-bm-seedv`.
 *  - Already at BM_SEED_V5: returned untouched, no write. At BM_SEED_V4: the v5 rows are appended.
 *  - Otherwise v2's seed mechanism runs first (missing BM_SEEDS ids are appended when the stored
 *    version is not v2's BM_SEED_V — never overwriting an edited row), then every row is remapped,
 *    then the v4 seed moves are applied to rows still on their v3 seed assignment, then the v5 rows
 *    are appended.
 *  - No stored rows: the remapped seed catalog is returned; the caller persists only on first edit.
 */
export function migrateBenchmarkRows(storedRows: unknown, storedSeedV?: string | null): BenchmarkMigration {
  const parsed = z.array(BmRowV3Schema).safeParse(storedRows);
  const stored = parsed.success ? parsed.data : null;
  if (stored && storedSeedV === BM_SEED_V5) return { rows: stored, seedV: BM_SEED_V5, changed: false };
  if (stored && storedSeedV === BM_SEED_V4) return { rows: appendV5Seeds(stored), seedV: BM_SEED_V5, changed: true };
  let base: BmRow[];
  if (!stored) base = BM_SEEDS; // v2 seed rows are a subset of the v3 row
  else if (storedSeedV === BM_SEED_V) base = stored;
  else {
    const have = new Set(stored.map((r) => r.id));
    base = [...stored, ...BM_SEEDS.filter((s) => !have.has(s.id))];
  }
  return { rows: appendV5Seeds(retagMovedSeeds(base.map(migrateBenchmarkRow))), seedV: BM_SEED_V5, changed: true };
}

/** Rows to show: hidden ones (retired levers) stay in storage but out of the table. */
export function visibleBenchmarkRows(rows: BmRow[]): BmRow[] {
  return rows.filter((r) => !r.hidden);
}
